<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="760px">
    <v-card class="profile-dialog-card entity-form-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-account-tie-outline</v-icon>
          </v-avatar>
          <div>
            <span>Teachers</span>
            <h2>New teacher</h2>
            <p>Add contact details and optionally enable teacher portal access.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close new teacher dialog" @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-account-outline</v-icon></span>
            <div>
              <h3>Teacher information</h3>
              <p>Basic contact details used throughout Taleem.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field v-model="Teacher.name" outlined dense autofocus
                :error-messages="nameErrors" :hide-details="nameErrors.length === 0"
                label="Full name" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="Teacher.phoneNumber" v-mask="'(###) ###-####'"
                outlined dense hide-details type="tel" label="Phone number" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="Teacher.email" outlined dense hide-details
                type="email" label="Email address" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="Teacher.description" outlined dense hide-details
                auto-grow rows="2" :label="$t('labels.description')" />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-map-marker-outline</v-icon></span>
            <div>
              <h3>Address</h3>
              <p>Optional mailing and location information.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field v-model="Teacher.address" outlined dense hide-details label="Street address" />
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field v-model="Teacher.city" outlined dense hide-details label="City" />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field v-model="Teacher.state" v-mask="'AA'" outlined dense hide-details label="State" />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field v-model="Teacher.zip" v-mask="'#####'" outlined dense hide-details label="ZIP code" />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section teacher-portal-access">
          <div class="teacher-portal-access__overview">
            <div class="teacher-portal-access__icon">
              <v-icon color="primary">mdi-monitor-account</v-icon>
            </div>
            <div>
              <h3>Teacher portal access</h3>
              <p>Allow this teacher to sign in and use the teacher panel.</p>
            </div>
            <v-switch v-model="Teacher.portalAccess" inset hide-details
              aria-label="Enable teacher portal access" @change="portalAccessChanged" />
          </div>

          <v-expand-transition>
            <div v-if="Teacher.portalAccess" class="teacher-portal-access__credentials">
              <v-text-field v-model="Teacher.userData.loginEmail" outlined dense
                type="email" label="Login email" prepend-inner-icon="mdi-email-outline"
                :error-messages="loginEmailErrors" :hide-details="loginEmailErrors.length === 0" />
              <v-text-field v-model="Teacher.userData.loginPassword" outlined dense
                :type="showPassword ? 'text' : 'password'" label="Temporary password"
                prepend-inner-icon="mdi-lock-outline"
                :error-messages="loginPasswordErrors" :hide-details="loginPasswordErrors.length === 0"
                :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append="showPassword = !showPassword" />
              <div class="teacher-portal-access__hint">
                <v-icon size="16" color="primary">mdi-information-outline</v-icon>
                Share these credentials securely with the teacher.
              </div>
            </div>
          </v-expand-transition>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <v-btn text @click="close()">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit()">
          <v-icon left small>mdi-account-plus-outline</v-icon>
          Create teacher
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import teacherService from "../service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "create-teacher",
  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      showPassword: false,
      portalTouched: false,
      dialog: false,
      Teacher: {
				userData: {
					loginEmail: null,
					loginPassword: null
				},
				portalAccess: false,
				LoginUserId: null,
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      },
    };
  },
  mixins: [validationMixin],
  validations: {
    Teacher: {
      name: {
        required,
      },
    },
  },
  watch: {
    createDialog(val) {
      this.dialog = val;
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    nameErrors() {
      const errors = [];
      if (!this.$v.Teacher.name.$dirty) return errors;
      if (!this.$v.Teacher.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
    loginEmailErrors() {
      if (!this.portalTouched || !this.Teacher.portalAccess) return [];
      return this.Teacher.userData.loginEmail ? [] : ["Login email is required"];
    },
    loginPasswordErrors() {
      if (!this.portalTouched || !this.Teacher.portalAccess) return [];
      return this.Teacher.userData.loginPassword ? [] : ["Temporary password is required"];
    },
  },
  methods: {
		portalAccessChanged() {
      if (!this.Teacher.portalAccess) {
        this.Teacher.userData = {
          loginEmail: null,
          loginPassword: null,
        };
        this.showPassword = false;
        this.portalTouched = false;
      } else if (!this.Teacher.userData.loginEmail && this.Teacher.email) {
        this.Teacher.userData.loginEmail = this.Teacher.email;
      }
		},
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Teacher = {
				userData: {
					loginEmail: null,
					loginPassword: null
				},
				portalAccess: false,
				LoginUserId: null,
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      };
      this.$v.$reset();
      this.showPassword = false;
      this.portalTouched = false;
    },
    submit() {
      this.$v.$touch();
      this.portalTouched = true;
      const portalInvalid =
        this.Teacher.portalAccess &&
        (!this.Teacher.userData.loginEmail || !this.Teacher.userData.loginPassword);
      if (this.$v.$invalid || portalInvalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        return teacherService
          .create(this.Teacher)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Teacher is created",
                });
              this.loading = false;
              this.$emit("submit");
              this.close();
            }
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err && err.data ? err.data.message : "Teacher could not be created",
              });
          });
      }
    },
  },
};
</script>

<style scoped></style>
